/**
 * Slice 47 — extras for guests, cleaner spots per party size.
 * Extras save on their own. Cleaner rows write only on Save.
 */
import { useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { guestSentence } from "@/lib/guestGuideCopy";
import {
  emptyLeaveOutPlan,
  firstFreeGuestCount,
  leaveOutRuleSentence,
  nextGuestCount,
  type LeaveOutPartyRow,
  type LeaveOutPlan,
  type LeaveOutSpot,
} from "@/lib/leaveOutPlan";

function newId(): string {
  return crypto.randomUUID();
}

function Stepper({
  value,
  onChange,
  min,
  max,
  muted,
}: {
  value: number;
  onChange: (n: number) => void;
  min: number;
  max: number;
  muted?: boolean;
}) {
  return (
    <div className="inline-flex h-11 items-center overflow-hidden rounded-xl border border-[#d7e0e6] bg-white">
      <button
        type="button"
        className="flex h-11 w-11 items-center justify-center text-[20px] text-[#15776d] hover:bg-[#f4f7f9]"
        onClick={() => onChange(Math.max(min, value - 1))}
        aria-label="Decrease"
      >
        −
      </button>
      <span
        className={cn(
          "min-w-[30px] text-center text-[17px] font-bold tabular-nums",
          muted ? "text-[#94a3b0]" : "text-[#0f1720]",
        )}
      >
        {value}
      </span>
      <button
        type="button"
        className="flex h-11 w-11 items-center justify-center text-[20px] text-[#15776d] hover:bg-[#f4f7f9]"
        onClick={() => onChange(Math.min(max, value + 1))}
        aria-label="Increase"
      >
        +
      </button>
    </div>
  );
}

function planKey(plan: LeaveOutPlan): string {
  return JSON.stringify(
    plan.rules.map((r) => ({
      guests: r.guests,
      spots: r.spots.map((s) => ({ qty: s.qty, place: s.place })),
    })),
  );
}

export default function LeaveOutRulesDrawer({
  itemName,
  itemPhoto,
  nowLabel,
  lowLabel,
  initialPlan,
  extrasPlace,
  extrasPhoto,
  saving,
  savingExtras,
  mobile,
  onCancel,
  onSave,
  onSaveExtras,
  onEditStock,
  onUploadPhoto,
}: {
  itemName: string;
  itemPhoto?: string | null;
  nowLabel: string;
  lowLabel: string;
  initialPlan: LeaveOutPlan;
  extrasPlace: string;
  extrasPhoto: string | null;
  saving: boolean;
  savingExtras: boolean;
  mobile: boolean;
  onCancel: () => void;
  onSave: (plan: LeaveOutPlan) => void;
  onSaveExtras: (place: string, photoUrl: string | null) => Promise<void>;
  onEditStock: () => void;
  onUploadPhoto: (file: File) => Promise<string | null>;
}) {
  const [plan, setPlan] = useState<LeaveOutPlan>(initialPlan);
  const [savedPlace, setSavedPlace] = useState(extrasPlace);
  const [savedPhoto, setSavedPhoto] = useState(extrasPhoto);
  const [editingExtras, setEditingExtras] = useState(false);
  const [draftPlace, setDraftPlace] = useState(extrasPlace);
  const [draftPhoto, setDraftPhoto] = useState<string | null>(extrasPhoto);
  const fileRef = useRef<HTMLInputElement>(null);
  const dirty = planKey(plan) !== planKey(initialPlan);

  const guestLine = (place: string) =>
    guestSentence({ kind: "inventory", name: itemName, qty: null, place });

  const patchRule = (ruleId: string, fn: (rule: LeaveOutPartyRow) => LeaveOutPartyRow) => {
    setPlan((prev) => ({
      rules: prev.rules.map((rule) => (rule.id === ruleId ? fn(rule) : rule)),
    }));
  };

  const addRule = () => {
    const used = plan.rules.map((r) => r.guests).filter((n): n is number => n != null);
    const spot: LeaveOutSpot = { id: newId(), qty: 1, place: "" };
    const next: LeaveOutPartyRow = { id: newId(), guests: firstFreeGuestCount(used), spots: [spot] };
    setPlan((prev) => ({
      rules: [...prev.rules, next].sort((a, b) => {
        if (a.guests == null) return -1;
        if (b.guests == null) return 1;
        return a.guests - b.guests;
      }),
    }));
  };

  const startExtras = () => {
    setDraftPlace(savedPlace);
    setDraftPhoto(savedPhoto);
    setEditingExtras(true);
  };

  const saveExtras = async () => {
    const place = draftPlace.trim();
    if (!place) return;
    try {
      await onSaveExtras(place, draftPhoto);
    } catch {
      return;
    }
    setSavedPlace(place);
    setSavedPhoto(draftPhoto);
    setEditingExtras(false);
  };

  return (
    <div
      className={cn(
        "flex h-full flex-col bg-white",
        mobile ? "w-full" : "w-[620px] shadow-[-18px_0_50px_rgba(15,23,32,0.16)]",
      )}
    >
      <div className="flex items-center gap-4 border-b border-[#e6edf1] px-6 py-[22px]">
        <div className="flex h-[60px] w-[60px] shrink-0 overflow-hidden rounded-[14px] bg-[#e3eaee]">
          {itemPhoto ? <img src={itemPhoto} alt="" className="h-full w-full object-cover" /> : null}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[22px] font-bold tracking-[-0.3px]">{itemName}</div>
          <div className="text-sm font-medium text-[#64748b]">
            Now {nowLabel} · Low at {lowLabel}
          </div>
        </div>
        <button type="button" onClick={onEditStock} className="text-sm font-bold text-[#15776d]">
          Edit stock
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-auto px-6 py-[22px]">
        <div
          className={cn(
            "flex flex-col gap-3 rounded-[14px] border bg-[#f8fafb] px-4 py-3.5",
            editingExtras ? "border-[#24a89a]" : "border-[#e6edf1]",
          )}
        >
          <div className="flex items-center gap-2.5">
            <div className="flex-1 text-[13px] font-bold tracking-[0.3px] text-[#64748b]">Extras for guests</div>
            {!editingExtras ? (
              <button type="button" onClick={startExtras} className="px-0.5 py-1.5 text-sm font-bold text-[#15776d]">
                Edit
              </button>
            ) : null}
          </div>
          {!editingExtras ? (
            <div className="flex items-center gap-3.5">
              <div className="h-[72px] w-24 shrink-0 overflow-hidden rounded-[10px] bg-[#dfe7ec]">
                {savedPhoto ? <img src={savedPhoto} alt="" className="h-full w-full object-cover" /> : null}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-base font-bold">{savedPlace.trim() || "Not set"}</div>
                <div className="text-[13.5px] font-medium text-[#64748b]">{guestLine(savedPlace)}</div>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-3.5">
                <div className="relative h-[90px] w-[120px] shrink-0 overflow-hidden rounded-[10px] bg-[#dfe7ec]">
                  {draftPhoto ? <img src={draftPhoto} alt="" className="absolute inset-0 h-full w-full object-cover" /> : null}
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="absolute bottom-1.5 left-1.5 rounded-full bg-white px-2 py-0.5 text-[12.5px] font-bold text-[#15776d]"
                  >
                    Replace
                  </button>
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  <div className="text-[12.5px] font-bold text-[#64748b]">Extras are in</div>
                  <input
                    value={draftPlace}
                    onChange={(e) => setDraftPlace(e.target.value)}
                    placeholder="the basket"
                    className="h-11 w-full rounded-[11px] border-[1.5px] border-[#24a89a] bg-white px-3 text-[15px] font-medium outline-none"
                  />
                  <div className="text-[13px] font-medium text-[#64748b]">{guestLine(draftPlace)}</div>
                </div>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (!file) return;
                  void onUploadPhoto(file).then((url) => {
                    if (url) setDraftPhoto(url);
                  });
                }}
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEditingExtras(false)}
                  className="flex h-11 items-center rounded-[11px] border border-[#e6edf1] bg-white px-4 text-sm font-bold text-[#475569]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={savingExtras || !draftPlace.trim()}
                  onClick={() => void saveExtras()}
                  className="flex h-11 items-center gap-2 rounded-[11px] bg-[#24a89a] px-[18px] text-sm font-bold text-white disabled:bg-[#cfd9de]"
                >
                  {savingExtras ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Save extras
                </button>
              </div>
            </>
          )}
        </div>

        <div className="flex flex-col gap-3">
          <div className="text-base font-bold">What cleaners set out</div>
          {plan.rules.map((rule) => {
            const used = plan.rules.map((r) => r.guests).filter((n): n is number => n != null && n !== rule.guests);
            return (
              <div key={rule.id} className="flex flex-col gap-2.5 rounded-[14px] border border-[#e6edf1] px-4 py-3.5">
                <div className="flex min-h-11 items-center gap-2.5">
                  {rule.guests == null ? (
                    <div className="text-[15.5px] font-bold">Every stay</div>
                  ) : (
                    <>
                      <div className="text-[15.5px] font-bold">If</div>
                      <Stepper
                        value={rule.guests}
                        min={1}
                        max={16}
                        onChange={(n) => {
                          if (n === rule.guests) return;
                          const dir = n > rule.guests! ? 1 : -1;
                          patchRule(rule.id, (r) => ({ ...r, guests: nextGuestCount(used, rule.guests!, dir) }));
                        }}
                      />
                      <div className="text-[15.5px] font-bold">guests</div>
                    </>
                  )}
                  <div className="flex-1" />
                  <button
                    type="button"
                    onClick={() => setPlan((prev) => ({ rules: prev.rules.filter((r) => r.id !== rule.id) }))}
                    className="px-1 py-2 text-[13.5px] font-medium text-[#8494a2]"
                  >
                    Remove
                  </button>
                </div>
                {rule.spots.map((spot) => (
                  <div key={spot.id} className="flex items-center gap-2.5">
                    <Stepper
                      value={spot.qty}
                      min={0}
                      max={20}
                      muted={spot.qty === 0}
                      onChange={(qty) =>
                        patchRule(rule.id, (r) => ({
                          ...r,
                          spots: r.spots.map((s) => (s.id === spot.id ? { ...s, qty } : s)),
                        }))
                      }
                    />
                    <input
                      value={spot.place}
                      onChange={(e) => {
                        const place = e.target.value;
                        patchRule(rule.id, (r) => ({
                          ...r,
                          spots: r.spots.map((s) => (s.id === spot.id ? { ...s, place } : s)),
                        }));
                      }}
                      placeholder="on the queen bed"
                      className="h-11 min-w-0 flex-1 rounded-xl border border-[#d7e0e6] bg-white px-3 text-[15px] font-medium outline-none"
                    />
                    <button
                      type="button"
                      aria-label="Remove place"
                      onClick={() =>
                        patchRule(rule.id, (r) => {
                          if (r.spots.length > 1) return { ...r, spots: r.spots.filter((s) => s.id !== spot.id) };
                          return { ...r, spots: [{ id: spot.id, qty: 1, place: "" }] };
                        })
                      }
                      className="flex h-11 w-9 shrink-0 items-center justify-center text-lg text-[#94a3b0]"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    patchRule(rule.id, (r) => ({
                      ...r,
                      spots: [...r.spots, { id: newId(), qty: 1, place: "" }],
                    }))
                  }
                  className="self-start py-2 text-sm font-bold text-[#15776d]"
                >
                  + Add a place
                </button>
                <div className="border-t border-[#eef2f5] pt-2.5 text-[14.5px] font-medium text-[#15776d]">
                  {leaveOutRuleSentence(rule)}
                </div>
              </div>
            );
          })}
          <button
            type="button"
            onClick={addRule}
            className="flex h-12 items-center justify-center rounded-xl border-[1.5px] border-dashed border-[#b9c6cf] text-[14.5px] font-bold text-[#15776d] hover:bg-[#f4f7f9]"
          >
            + Add a guest count
          </button>
        </div>
      </div>

      <div className="flex justify-end gap-2.5 border-t border-[#e6edf1] px-6 py-4">
        <button
          type="button"
          onClick={onCancel}
          className="flex h-12 items-center rounded-xl border border-[#e6edf1] px-5 text-[15px] font-bold text-[#475569] hover:bg-[#f4f7f9]"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={() => onSave(plan)}
          className="flex h-12 items-center gap-2.5 rounded-xl bg-[#24a89a] px-7 text-[15px] font-bold text-white disabled:bg-[#cfd9de]"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Save
        </button>
      </div>
    </div>
  );
}

export function initialPlanForItem(saved: LeaveOutPlan | null | undefined): LeaveOutPlan {
  if (saved && saved.rules.length > 0) return saved;
  return emptyLeaveOutPlan();
}
